import { useEffect, useState } from "react";
import { listenToNchPortalQuestionnaire } from "../api/firebase/firestoreService";
import { MedicalQuestion } from "../data-model/medicalQuestion";

interface UseFetchQuestionnaireProps {
  orderId: string;
}

interface QuestionnaireData {
  data: {
    question_tree: MedicalQuestion[];
  };
  loading: boolean;
  error: string | null;
}

/**
 * Live-listens to the NCH portal questionnaire tree for a given order.
 *
 * Sets up a Firestore `onSnapshot` listener that automatically updates
 * whenever the questionnaire document changes. The listener is cleaned
 * up when the component unmounts or the `orderId` changes.
 *
 * @param props.orderId - The medical PA order ID used to locate the
 *   questionnaire document in Firestore.
 */
const useFetchQuestionnaire = (props: UseFetchQuestionnaireProps) => {
  const { orderId } = props;
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData>(
    {
      data: {
        question_tree: [],
      },
      loading: true,
      error: null,
    },
  );

  useEffect(() => {
    if (!orderId) {
      setQuestionnaireData({
        data: { question_tree: [] },
        loading: false,
        error: null,
      });
      return;
    }

    setQuestionnaireData((prev) => ({ ...prev, loading: true, error: null }));

    const unsubscribe = listenToNchPortalQuestionnaire(
      orderId,
      (questions) => {
        setQuestionnaireData({
          data: { question_tree: questions },
          loading: false,
          error: null,
        });
      },
      (error) => {
        setQuestionnaireData((prev) => ({
          ...prev,
          loading: false,
          error: error.message,
        }));
      },
    );

    return unsubscribe;
  }, [orderId]);

  const hasQuestions = questionnaireData.data.question_tree.length > 0;

  return { ...questionnaireData, hasQuestions };
};

export default useFetchQuestionnaire;
